import { newGuid } from '../common/new-guid';
import { clickedInsideElementWithClass } from '../dom/clicked-inside-element-with-class';
import { safeApplyCssUnit } from '../dom/safe-apply-css-value';
import { EventDispatcher } from '../events/event-dispatcher';
import { KEYCODES } from '../keycodes';
import { addTransitionEndListener } from '../transition/add-transition-end-listener';
import { OverlayOptions } from './overlay-options';

export class Overlay {
	overlayElement: HTMLElement | undefined;
	onAttached = new EventDispatcher();
	onDetached = new EventDispatcher();
	beforeOpen = new EventDispatcher();
	beforeClose = new EventDispatcher();
	afterOpened = new EventDispatcher();
	afterClosed = new EventDispatcher();
	onDisposed = new EventDispatcher();
	onKeyboardEvent = new EventDispatcher<KeyboardEvent>();
	backdropClick = new EventDispatcher<MouseEvent>();
	options: OverlayOptions;
	attached = false;
	isOpen = false;

	private uuid = newGuid();
	private overlayContainerElement: HTMLElement | undefined;
	private backdropElement: HTMLElement | null | undefined;

	constructor(public portalElement: HTMLElement, options?: OverlayOptions) {
		this.options = new OverlayOptions(options);
		options?.scrollStrategy?.attach(this);
		if (this.options.portalClassName) {
			this.portalElement.classList.add(this.options.portalClassName);
		}

		if (this.options.closeOnClick) {
			this.portalElement.addEventListener('click', () => this.close());
		}
	}

	create(): Overlay {
		const topLayer = this.getTopLayer();
		const overlayContainerElement = topLayer.querySelector<HTMLElement>(`.${this.options.containerClassName}`);

		if (overlayContainerElement) {
			this.overlayContainerElement = overlayContainerElement;
		} else {
			this.overlayContainerElement = document.createElement('div');
			this.overlayContainerElement.classList.add(this.options.containerClassName || '');

			if (topLayer === document.body) {
				document.body.insertBefore(this.overlayContainerElement, document.body.lastElementChild);
			} else {
				topLayer.appendChild(this.overlayContainerElement);
			}
		}

		this.overlayElement = document.createElement('div');
		this.overlayElement.id = `${this.options.className}-${this.uuid}`;
		if (this.options.className) {
			this.overlayElement.classList.add(this.options.className);
		}
		if (this.options.overlayClassName) {
			this.overlayElement.classList.add(this.options.overlayClassName);
		}

		this.overlayContainerElement.appendChild(this.overlayElement);

		return this;
	}

	detachBackdrop() {
		if (!this.backdropElement) {
			return;
		}

		const backdropToDetach = this.backdropElement;

		const finishDetach = () => {
			backdropToDetach?.parentNode?.removeChild(backdropToDetach);

			if (this.backdropElement === backdropToDetach) {
				this.backdropElement = null;
			}
		};

		backdropToDetach.classList.remove(this.options.showingClassName || '');
		backdropToDetach.classList.remove(this.options.backdropClassName || '');
		addTransitionEndListener(backdropToDetach, finishDetach, 500);
		backdropToDetach.style.pointerEvents = 'none';
	}

	updatePosition() {
		this.options.positionStrategy?.apply();
	}

	updateSize(sizeConfig: OverlaySizeConfig) {
		this.options = { ...this.options, ...sizeConfig };
		this.updateElementSize();
	}

	open() {
		this.attach();
		this.updatePosition();
		this.portalElement.classList.add(this.options.showingClassName || '');

		this.beforeOpen.dispatch();
		this.isOpen = true;

		const finishOpen = () => {
			this.portalElement.classList.remove(this.options.showingClassName || '');
			this.portalElement.classList.add(this.options.visibleClassName || '');
			this.afterOpened.dispatch();
		};

		addTransitionEndListener(this.portalElement, finishOpen, this.options.transitionDuration);
	}

	close() {
		this.detach();

		this.portalElement.classList.add(this.options.hidingClassName || '');
		this.beforeClose.dispatch();
		if (this.options.detachBackdropBeforeClose) {
			this.detachBackdrop();
		}

		const finishClose = () => {
			this.portalElement.classList.remove(this.options.hidingClassName || '');
			this.portalElement.classList.remove(this.options.showingClassName || '');
			this.portalElement.classList.remove(this.options.visibleClassName || '');
			this.isOpen = false;
			this.afterClosed.dispatch();
			if (this.options.disposeAfterClose) {
				this.dispose();
			}
		};

		addTransitionEndListener(this.portalElement, finishClose, this.options.transitionDuration);
	}

	dispose() {
		if (this.portalElement && this.portalElement.parentElement) {
			this.portalElement.parentElement.removeChild(this.portalElement);
		}

		this.options.positionStrategy?.dispose();
		this.options.scrollStrategy?.disable();
		this.detachBackdrop();
		document.body.removeEventListener('keydown', this.keydownListener, true);
		this.onDisposed.dispatch();
	}

	private getTopLayer(): HTMLElement {
		// atm there is no other way to handle #top-layer correctly...
		const openDialogs = Array.from(document.querySelectorAll('dialog')).filter(o => o.open);

		// return last opened dialog?!
		if (openDialogs.length) {
			return openDialogs.slice(-1)[0];
		}

		return document.body;
	}

	private attach() {
		this.options.positionStrategy?.attach(this);
		this.options.scrollStrategy?.enable();
		this.overlayElement?.appendChild(this.portalElement);

		this.updateElementSize();

		if (this.overlayElement) {
			this.overlayElement.style.pointerEvents = 'auto';
		}

		this.attachBackdrop();

		this.attached = true;
		document.body.addEventListener('keydown', this.keydownListener, true);
		this.onAttached.dispatch();
	}

	private detach() {
		if (!this.attached) {
			return;
		}

		this.detachBackdrop();
		if (this.overlayElement) {
			this.overlayElement.style.pointerEvents = 'none';
		}
		this.options.positionStrategy?.detach();
		this.options.scrollStrategy?.disable();
		document.body.removeEventListener('keydown', this.keydownListener, true);
		this.onDetached.dispatch();
	}

	private attachBackdrop() {
		if (!this.options.hasBackdrop) {
			return;
		}

		this.backdropElement = document.createElement('div');
		this.backdropElement.classList.add(this.options.baseBackdropClassName || '');

		this.backdropElement.classList.add(this.options.backdropClassName || '');
		if (this.overlayElement) {
			this.overlayElement.parentElement?.insertBefore(this.backdropElement, this.overlayElement);
		}

		this.backdropElement.addEventListener('click', event => this.handleBackdropClick(event));

		requestAnimationFrame(() => {
			this.backdropElement?.classList.add(this.options.showingClassName || '');
		});
	}

	private updateElementSize() {
		if (!this.overlayElement) {
			return;
		}

		safeApplyCssUnit(this.overlayElement, 'width', this.options.width);
		safeApplyCssUnit(this.overlayElement, 'height', this.options.height);
		safeApplyCssUnit(this.overlayElement, 'minWidth', this.options.minWidth);
		safeApplyCssUnit(this.overlayElement, 'minHeight', this.options.minHeight);
		safeApplyCssUnit(this.overlayElement, 'maxWidth', this.options.maxWidth);
		safeApplyCssUnit(this.overlayElement, 'maxHeight', this.options.maxHeight);
	}

	private handleBackdropClick(event: MouseEvent) {
		if (this.options.className && !clickedInsideElementWithClass(event, this.options.className)) {
			this.backdropClick.dispatch(event);
			if (this.options.closeOnBackdropClick) {
				this.close();
			}
		}
	}

	private keydownListener = (event: KeyboardEvent) => {
		this.onKeyboardEvent.dispatch(event);
		if (this.options.closeOnEscPressed) {
			if (event.keyCode === KEYCODES.ESCAPE) {
				this.close();
			}
		}
	};
}

export function overlay(element: HTMLElement, options?: OverlayOptions) {
	return new Overlay(element, options).create();
}

export interface OverlaySizeConfig {
	width?: number | string;
	height?: number | string;
	minWidth?: number | string;
	minHeight?: number | string;
	maxWidth?: number | string;
	maxHeight?: number | string;
}
